import React, { useContext, useState, useCallback } from "react";
import moment from "moment";
import clsx from "clsx";
import Select from "react-select";
import { FaPencilAlt } from "react-icons/fa";

// IntersectionObserver polyfill
import "intersection-observer";
import { useInView } from "react-intersection-observer";
import Switch from "react-switch";

import { QueryBuilderContext } from "contexts/QueryBuilderContextProvider";

import {
  QueryBuilderSegmentsLoader,
  QueryBuilderLineChartLoader,
  QueryBuilderBarChartLoader,
  QueryBuilderTableLoader,
} from "components/QueryBuilder/Elements/QueryLoaders";

import ViewQueryButton from "components/QueryBuilder/ViewQueryButton";

import QueryBuilderSegmentationTable from "components/QueryBuilder/Visualization/QueryBuilderSegmentationTable";
import QueryBuilderLineChart from "components/QueryBuilder/Visualization/QueryBuilderLineChart";
import QueryBuilderStackedBarChart from "components/QueryBuilder/Visualization/QueryBuilderStackedBarChart";
import QueryBuilderBarChart from "components/QueryBuilder/Visualization/QueryBuilderBarChart";
import QueryBuilderTable from "components/QueryBuilder/Visualization/QueryBuilderTable";
import QueryBuilderPivotTable from "components/QueryBuilder/Visualization/QueryBuilderPivotTable";

// icons
import imgSegmented from "i/query-builder/segmented.svg";
import imgLine from "i/query-builder/line.svg";
import imgBar from "i/query-builder/bar.svg";
import imgStackBar from "i/query-builder/stack-bar.svg";
import imgTable from "i/query-builder/table.svg";
import imgSegmentedGray from "i/query-builder/segmented-gray.svg";
import imgLineGray from "i/query-builder/line-gray.svg";
import imgBarGray from "i/query-builder/bar-gray.svg";
import imgStackBarGray from "i/query-builder/stack-bar-gray.svg";
import imgTableGray from "i/query-builder/table-gray.svg";

import pickaxe_insights_logo from "i/brand/pickaxe_small_circle.svg";
import { AppContext } from "contexts/AppContextProvider";

import {
  hasMetrics,
  getQueryParam,
  QueryFiltersFromURL,
  querySegmentTracking,
} from "components/QueryBuilder/Utils/QueryBuilderUtils";
import {
  exportReportEnum,
  getExportReportConfig,
} from "components/QueryBuilder/Elements/QueryExportData";
import { predictionLabels } from "components/Utils/ChartsDataUtils";

const QueryResults = (props) => {
  const { currentProject } = useContext(AppContext).state;
  const { state, dispatch } = useContext(QueryBuilderContext);
  const { data, loadingData, queryMetrics } = state;
  const currentTenantSlug = localStorage.getItem("defaultTenantSlug");
  let { predictions } = state;
  const [pastedQuery] = useState(
    props.pastedQuery ? JSON.parse(props.pastedQuery) : null
  );

  // Get initial tab from short url or set segments as default
  const [currentQueryChartType, setCurrentQueryChartType] = useState(
    props.pastedQuery
      ? getQueryParam(pastedQuery, "chartType")
      : state.queryOptions.chartType
  );

  const [visualization, setVisualization] = useState([
    {
      type: "segments",
      isVisible: currentQueryChartType === "segments",
    },
    {
      type: "line-chart",
      isVisible: currentQueryChartType === "line-chart",
    },
    {
      type: "bar-chart",
      isVisible: currentQueryChartType === "bar-chart",
    },
    {
      type: "bar-chart-stacked",
      isVisible: currentQueryChartType === "bar-chart-stacked",
    },
    {
      type: "table",
      isVisible: currentQueryChartType === "table",
    },
    {
      type: "pivot-table",
      isVisible: currentQueryChartType === "pivot-table",
    },
  ]);

  const [selectDefaultTrendedGranularity, SetSelectDefaultTrendedGranularity] =
    useState(true);

  const [queryStickyBarRef, queryStickyBarRefinView] = useInView({
    threshold: 0,
  });

  // TODO: simplify
  const granularityHashDefault = pastedQuery
    ? pastedQuery.find((o) => o.granularity).granularity
    : false;

  const granularityLabel = granularityHashDefault
    ? granularityHashDefault.charAt(0).toUpperCase() +
      granularityHashDefault.slice(1)
    : false;

  const [trendedGranularity, setTrendedGranularity] = useState({
    label: granularityLabel || "Day",
    value: granularityHashDefault || "day",
  });

  // FIXME: TBD or to be used in the future.
  const getDefaultTrendedGranularity = () => {
    let chartsGranularity = {};

    if (state && state.query && state.granularityOptions) {
      const options = state.granularityOptions;
      const startDate = moment(state.queryOptions.startDate);
      const endDate = moment(state.query.endDate);
      const days = endDate.diff(startDate, "days");

      if (options) {
        if (days <= 7) {
          // days
          chartsGranularity = options.find((option) => option.value === "day");
        } else if (days > 7 && days <= 90) {
          // weeks
          chartsGranularity = options.find(
            (option) => option.value === "week" || option.value === "month"
          );
        } else if (days > 90) {
          // months
          chartsGranularity = options.find(
            (option) => option.value === "month"
          );
        }
      }

      // Update granularity select
      setTrendedGranularity({
        label: chartsGranularity.label,
        value: chartsGranularity.value,
      });

      return chartsGranularity;
    }
  };
  const toggleVisualization = (type) => {
    const data = [...visualization];

    data.forEach((graph, i) =>
      type === graph.type
        ? (visualization[i].isVisible = true)
        : (visualization[i].isVisible = false)
    );

    setCurrentQueryChartType(type);
    setVisualization(data);
    // Set default granularity based on date ranges (only once)
    if (
      selectDefaultTrendedGranularity &&
      type !== "segments" &&
      type !== "pivot-table"
    ) {
      // Make sure we run only once
      SetSelectDefaultTrendedGranularity(false);

      dispatch({
        type: "SET-QUERY-OPTIONS",
        payload: {
          ...state.queryOptions,
          listMetricsIds: state.query.metrics || [],
          chartType: type,
          granularity:
            state.queryOptions.granularityByUser ||
            getDefaultTrendedGranularity().value,
        },
      });
    } else {
      // Default to all for segments and tables or use selected granularity
      let granularity = trendedGranularity.value;

      if (type === "segments" || type === "pivot-table") {
        granularity = "all";
      }

      dispatch({
        type: "SET-QUERY-OPTIONS",
        payload: {
          ...state.queryOptions,
          listMetricsIds: state.query.metrics || [],
          chartType: type,
          granularity: granularity,
        },
      });
    }
  };

  let showSegments = visualization.find((o) => o.type === "segments").isVisible;
  let showLineChart = visualization.find(
    (o) => o.type === "line-chart"
  ).isVisible;
  let showBarChart = visualization.find(
    (o) => o.type === "bar-chart"
  ).isVisible;
  let showStackedBarChart = visualization.find(
    (o) => o.type === "bar-chart-stacked"
  ).isVisible;
  let showTable = visualization.find((o) => o.type === "table").isVisible;
  let showPivotTable = visualization.find(
    (o) => o.type === "pivot-table"
  ).isVisible;
  const dimensions = [];
  const dimensionMap = {};
  const dim_keys = [];
  const metrics = [];
  const metricMap = {};
  const dataGroupedByDate = {}; // need for line and bar chart

  // const startDate = moment(state.queryOptions.startDate).format("YYYY-MM-DD");
  // const endDate = moment(state.queryOptions.endDate).format("YYYY-MM-DD");

  const friendlyDateName = props.dateRanges.find(
    (date) =>
      state.queryOptions.startDate === date.from &&
      state.queryOptions.endDate === date.to
  );

  let currentDate =
    moment(state.queryOptions.startDate).format("MM/DD/YY") +
    "-" +
    moment(state.queryOptions.endDate).format("MM/DD/YY");
  const pastedQueryFilters = pastedQuery
    ? QueryFiltersFromURL(pastedQuery)
    : [];

  if (friendlyDateName && friendlyDateName.name !== undefined) {
    currentDate = friendlyDateName.name;
  }
  if (state && state.query && state.data.length > 0) {
    // Find All Metrics
    data.forEach((datum) => {
      // sort in correct order
      datum.metrics = state.query.metrics.map((id) => ({
        ...datum.metrics.find((d) => d.id === id),
      }));
      // get unique metrics
      datum.metrics.forEach((metric) => {
        if (!metrics.includes(metric.id)) {
          metrics.push(metric.id);
          const currentMetric = queryMetrics.find(
            (qM) => qM.id?.toString() === metric.id?.toString()
          );

          metricMap[metric.id] = {
            name: metric.id,
            friendlyName: metric.friendlyName,
            // set the value type from the source `queryMetrics` since it doesn't come from the
            // result data...
            valueType: currentMetric ? currentMetric.valueType : undefined,
          };
        }
      });

      if (datum.dimensions && state.query.dimensions) {
        // sort in correct order
        datum.dimensions = state.query.dimensions.map((id) => ({
          ...datum.dimensions.find((d) => d.id === id),
        }));
        // get unique dimensions
        datum.dimensions.forEach((dim) => {
          if (!dim.name) return;

          // TODO: nedd fix empty values on BE
          if (!dim.value) {
            dim.value = "undefined";
          }

          if (!dim_keys.includes(dim.name)) {
            dim_keys.push(dim.name);

            dimensions.push({
              id: dim.id,
              name: dim.name,
              friendlyName: dim.friendlyName,
              title: dim.friendlyName,
            });
            dimensionMap[dim.name] = [];
          }
          if (dimensionMap[dim.name].indexOf(dim.value) < 0)
            // dimensionMap[dim.name].push(dim.valueFriendlyName);
            dimensionMap[dim.name].push(dim.value);
        });
      }

      // ONLY if the data wasn't grouped by date yet...
      if (!dataGroupedByDate[datum.timestamp])
        // set a new array for the grouper
        dataGroupedByDate[datum.timestamp] = [];
      // but ALWAYS add the dimension inside the date grouper
      dataGroupedByDate[datum.timestamp].push(datum);
    });

    // merge the QB data with prediction data
    if (state && predictions.length > 0) {
      predictions.forEach((prediction) => {
        prediction.metrics.forEach((metric) => {
          // don't allow to add repeated metrics
          if (!metrics.includes(metric.id + ".pred")) {
            [".pred", ".predMinMax", ".accuracy", ".deviation"].forEach(
              (predValueType) => {
                metrics.push(metric.id + predValueType);

                const currentMetric = queryMetrics.find(
                  (qM) => qM.id?.toString() === metric.id?.toString()
                );

                metricMap[metric.id + predValueType] = {
                  name: metric.id + predValueType,
                  friendlyName: predictionLabels[predValueType],
                  // set the value type from the source `queryMetrics` since it doesn't come from the
                  // result data...
                  valueType: currentMetric
                    ? currentMetric.valueType
                    : undefined,
                };
              }
            );
          }
        });

        if (state.query.dimensions?.length) {
          prediction.dimensions.forEach((dim) => {
            if (!dim_keys.includes(dim.name)) {
              dim_keys.push(dim.name);
              dimensions.push(dim);
              dimensionMap[dim.name] = [];
            }

            if (dimensionMap[dim.name].indexOf(dim.value) < 0)
              // dimensionMap[dim.name].push(dim.valueFriendlyName);
              dimensionMap[dim.name].push(dim.value);
          });
        }

        if (!dataGroupedByDate[prediction.timestamp])
          dataGroupedByDate[prediction.timestamp] = [];
        dataGroupedByDate[prediction.timestamp].push(prediction);
      });
    }

    dimensions.forEach((dim) => {
      return (dim.values = dimensionMap[dim.name]);
    });
  }

  const handleGranularityChange = (selectedOption) => {
    setTrendedGranularity({
      label: selectedOption.label,
      value: selectedOption.value,
    });

    // dispatch({
    //   type: "SET-QUERY",
    //   payload: { ...state.query, granularity: selectedOption.value },
    // });

    dispatch({
      type: "SET-QUERY-OPTIONS",
      payload: {
        ...state.queryOptions,
        listMetricsIds: state.query.metrics || [],
        granularity: selectedOption.value,
        granularityByUser: selectedOption.value,
      },
    });
  };

  /**
   *  Chart's DOM node needed for exports
   */
  const getRef = useCallback(
    (node) => {
      if (node !== null) {
        dispatch({
          type: "SET-REF-REPORT-ELEMENT",
          payload: { current: node },
        });
      }
    },
    [dispatch]
  );

  const renderVisualization = () => {
    if (loadingData) {
      if (currentQueryChartType === "segments") {
        return <QueryBuilderSegmentsLoader />;
      } else if (currentQueryChartType === "line-chart") {
        return <QueryBuilderLineChartLoader />;
      } else if (currentQueryChartType === "bar-chart") {
        return <QueryBuilderBarChartLoader />;
      } else if (currentQueryChartType === "bar-chart-stacked") {
        return <QueryBuilderBarChartLoader />;
      } else if (currentQueryChartType === "table") {
        return <QueryBuilderTableLoader />;
      } else if (currentQueryChartType === "pivot-table") {
        return <QueryBuilderTableLoader />;
      }
    } else if (data.length === 0) {
      querySegmentTracking(
        "query-no-data",
        state,
        data,
        currentTenantSlug,
        pastedQueryFilters,
        currentDate,
        localStorage.defaultTenantID,
        currentProject
      );

      return <QueryBuilderSegmentsLoader noData={true} />;
    } else if (data.error) {
      querySegmentTracking(
        "error",
        state,
        data,
        currentTenantSlug,
        pastedQueryFilters,
        currentDate,
        exportConfig().options,
        localStorage.defaultTenantID,
        currentProject
      );

      return (
        <div className="d-flex flex-column justify-content-center align-items-center text-center">
          <p className="loader-message text-danger pb-0 mb-1">
            <b>{data.errorMessage}</b>
          </p>
          <small className="loader-message text-secondary">
            {data.errorCode} - {data.internalErrorCode} <br />
            {moment(data.timestamp).format("MMM D, YYYY, HH:mmA")}
          </small>
        </div>
      );
    }

    if (data.length > 0) {
      querySegmentTracking(
        "query-data",
        state,
        data,
        currentTenantSlug,
        pastedQueryFilters,
        currentDate,
        exportConfig().options,
        localStorage.defaultTenantID,
        currentProject
      );

      // format yAxis based in the metric value type rather than having it set by a default formatter
      const formatType =
        metrics?.length > 0 && Object.keys(metricMap).length > 0
          ? metricMap[metrics[0]].valueType
          : undefined;

      if (currentQueryChartType === "segments") {
        return (
          <QueryBuilderSegmentationTable
            data={data}
            dataGroupedByDate={dataGroupedByDate}
            dimensions={dimensions}
            metrics={metrics}
            queryMetrics={queryMetrics}
          />
        );
      }

      if (currentQueryChartType === "line-chart") {
        return (
          <QueryBuilderLineChart
            ref={getRef}
            dataGroupedByDate={dataGroupedByDate}
            metrics={metrics}
            metricMap={metricMap}
            dimensions={dimensions}
            granularity={trendedGranularity}
            startDate={state.queryOptions.startDate}
            endDate={state.queryOptions.endDate}
            formatType={formatType}
          />
        );
      }

      if (currentQueryChartType === "bar-chart") {
        return (
          <QueryBuilderBarChart
            ref={getRef}
            dataGroupedByDate={dataGroupedByDate}
            metrics={metrics}
            metricMap={metricMap}
            dimensions={dimensions}
            granularity={trendedGranularity}
            startDate={state.queryOptions.startDate}
            endDate={state.queryOptions.endDate}
            formatType={formatType}
          />
        );
      }

      if (currentQueryChartType === "bar-chart-stacked") {
        return (
          <QueryBuilderStackedBarChart
            ref={getRef}
            dataGroupedByDate={dataGroupedByDate}
            metrics={metrics}
            metricMap={metricMap}
            dimensions={dimensions}
            granularity={trendedGranularity}
            startDate={state.queryOptions.startDate}
            endDate={state.queryOptions.endDate}
            formatType={formatType}
          />
        );
      }

      if (currentQueryChartType === "table") {
        return (
          <QueryBuilderTable
            ref={getRef}
            dataGroupedByDate={dataGroupedByDate}
            data={data}
            dimensions={dimensions}
            queryMetrics={queryMetrics}
            metrics={metrics}
            startDate={state.queryOptions.startDate}
            endDate={state.queryOptions.endDate}
          />
        );
      }
      if (currentQueryChartType === "pivot-table") {
        return (
          <QueryBuilderPivotTable
            ref={getRef}
            data={data}
            dimensions={dimensions}
            queryMetrics={queryMetrics}
            metrics={metrics}
            startDate={state.queryOptions.startDate}
            endDate={state.queryOptions.endDate}
          />
        );
      }
    }
  };

  const granularitySelectStyles = {
    control: (styles) => ({
      ...styles,
      boxShadow: "none",
      fontColor: "#adb5bd",
      "&:hover": {
        color: "#000",
      },
      borderTop: "1px solid #e9ecef",
      borderLeft: "1px solid #e9ecef",
      borderRight: "1px solid #e9ecef",
      borderBottom: "1px solid #e9ecef",
      borderRadius: "2px",
      fontSize: "0.875rem",
      minHeight: "33px",
      padding: "1px 5px",
      width: "120px",
      "> div": {
        padding: 0,
      },
      "> div > div": {
        padding: "3px",
      },
    }),
    option: (styles) => {
      return {
        ...styles,
        backgroundColor: "#fff",
        cursor: "pointer",
        color: "#adb5bd",
        ":hover": {
          color: "#fff",
          backgroundColor: "#fda796",
        },
        fontSize: "0.875rem",
        textAlign: "left",
      };
    },
    menu: (styles) => ({ ...styles, width: "100px" }),
  };

  let queryTextBarStyle = clsx("query-insights-bar", {
    "d-block": !queryStickyBarRefinView,
    "d-none": queryStickyBarRefinView,
  });

  const renderInsightsIndicator = () => {
    const insightsCount =
      state &&
      state.insightsFeed &&
      state.insightsFeed
        .map((i) => i.data)
        .reduce((prev, next) => prev.concat(next), []).length;

    if (state && state.insightsFeedLoading) {
      return (
        <span className="query-insights-indicator query-insights-indicator--loading d-flex align-items-center ml-2 font-weight-bold animated infinite flash delay-2s">
          Discovering Insights...
        </span>
      );
    } else if (insightsCount > 0) {
      return (
        <span className="query-insights-indicator query-insights-indicator--loaded d-flex align-items-center ml-2 font-weight-bold animated flash">
          <img
            src={pickaxe_insights_logo}
            alt="Insights"
            className="mr-1 pickaxe_insights_logo"
          />
          {insightsCount} Insight{insightsCount > 1 ? "s" : ""} Discovered
        </span>
      );
    } else if (insightsCount === 0) {
      return (
        <span className="query-insights-indicator query-insights-indicator--loading d-flex align-items-center ml-2 font-weight-bold">
          No Insights were discovered
        </span>
      );
    } else {
      return null;
    }
  };

  const exportConfig = () => {
    if (showSegments || showTable || showPivotTable) {
      return getExportReportConfig(exportReportEnum.TABLE);
    }
    if (showLineChart) {
      return getExportReportConfig(exportReportEnum.LINE);
    }
    if (showBarChart) {
      return getExportReportConfig(exportReportEnum.BAR);
    }
    if (showStackedBarChart) {
      return getExportReportConfig(exportReportEnum.STACKED_BAR);
    }
    return {};
  };

  const showUncertainty =
    ["table", "pivot-table", "bar-chart-stacked"].indexOf(
      state.queryOptions.chartType
    ) === -1;
  return (
    <div className="row mt-4">
      <div className="col-12 mb-2">
        {
          <div className="row">
            <div className="col-10 d-flex text-left pl-4">
              {
                <>
                  <span className="query-option-border">
                    <ViewQueryButton
                      text="Segmented"
                      type="segments"
                      imgSrc={imgSegmentedGray}
                      imgSrcActive={imgSegmented}
                      imgStyle={{ marginBottom: "3px" }}
                      iconWrapperClass={"ml-2 pointer-events-none"}
                      iconAlign="right"
                      handleToggle={() => toggleVisualization("segments")}
                      active={showSegments}
                      query-option-border={false}
                      disabled={!hasMetrics(state)}
                    />
                  </span>

                  <span className="query-option-border">
                    <span
                      className={
                        hasMetrics(state) &&
                        (state.query.chartType === "line-chart" ||
                          state.query.chartType === "bar-chart" ||
                          state.query.chartType === "bar-chart-stacked")
                          ? "query-option-label-active pl-2"
                          : "query-option-label pl-2"
                      }
                    >
                      Trended
                    </span>
                    <ViewQueryButton
                      text=""
                      type="line-chart"
                      imgSrc={imgLineGray}
                      imgSrcActive={imgLine}
                      handleToggle={() => toggleVisualization("line-chart")}
                      iconWrapperClass={"pointer-events-none"}
                      active={showLineChart}
                      border={false}
                      disabled={!hasMetrics(state)}
                    />
                    <ViewQueryButton
                      text=""
                      type="bar-chart"
                      imgSrc={imgBarGray}
                      imgSrcActive={imgBar}
                      imgStyle={{ marginBottom: "1px" }}
                      iconWrapperClass={"mb-1 pointer-events-none"}
                      handleToggle={() => toggleVisualization("bar-chart")}
                      active={showBarChart}
                      border={false}
                      disabled={!hasMetrics(state)}
                    />
                    <ViewQueryButton
                      text=""
                      type="bar-chart-stacked"
                      imgSrc={imgStackBarGray}
                      imgSrcActive={imgStackBar}
                      imgStyle={{ marginBottom: "2px" }}
                      iconWrapperClass={"mb-1 pointer-events-none"}
                      handleToggle={() =>
                        toggleVisualization("bar-chart-stacked")
                      }
                      active={showStackedBarChart}
                      border={false}
                      disabled={!hasMetrics(state)}
                    />
                  </span>

                  <span className="query-option-border">
                    <ViewQueryButton
                      text="Data"
                      type="table"
                      imgSrc={imgTableGray}
                      imgSrcActive={imgTable}
                      imgStyle={{ marginBottom: "1px", marginLeft: "4px" }}
                      iconWrapperClass={"mr-2 pointer-events-none"}
                      //iconAlign="left"
                      handleToggle={() => toggleVisualization("table")}
                      active={showTable}
                      border={false}
                      disabled={!hasMetrics(state)}
                    />
                  </span>

                  <span className="query-option-border">
                    <ViewQueryButton
                      text="Pivot Table"
                      type="pivot-table"
                      imgSrc={imgTableGray}
                      imgSrcActive={imgTable}
                      imgStyle={{ marginBottom: "1px", marginLeft: "4px" }}
                      iconWrapperClass={"mr-2 pointer-events-none"}
                      //iconAlign="left"
                      handleToggle={() => toggleVisualization("pivot-table")}
                      active={showPivotTable}
                      border={false}
                      disabled={!hasMetrics(state)}
                    />
                  </span>

                  {renderInsightsIndicator()}
                </>
              }
              {state.loadPredictions && showUncertainty && (
                <div className="query-predictions-switch ml-auto text-right">
                  <label>
                    <span>View Uncertainty</span>
                    <Switch
                      onChange={(checked) =>
                        dispatch({
                          type: "SET-VIEW-UNCERTAINTY",
                          payload: checked,
                        })
                      }
                      checked={state.loadPredictions && state.viewUncertainty}
                      disabled={!state.loadPredictions}
                      className="react-switch"
                      uncheckedIcon
                      checkedIcon
                      height={18}
                      width={36}
                      activeBoxShadow="0 0 2px 3px"
                      onColor="#16de85"
                    />
                  </label>
                </div>
              )}

              {/* Hide granularity on segments and tables */}
              {currentQueryChartType !== "segments" &&
                currentQueryChartType !== "pivot-table" && (
                  <div
                    className={clsx({
                      "ml-auto": !state.loadPredictions || !showUncertainty,
                    })}
                  >
                    {state && state.granularityOptions.length > 0 ? (
                      <Select
                        options={state.granularityOptions.filter(
                          (o) => o.value !== "all"
                        )}
                        closeOnSelect={true}
                        isSearchable={false}
                        isClearable={false}
                        defaultValue={trendedGranularity}
                        styles={granularitySelectStyles}
                        getOptionLabel={(d) => d.label}
                        getOptionValue={(d) => d.value}
                        components={{
                          IndicatorSeparator: () => null,
                        }}
                        onChange={handleGranularityChange}
                      />
                    ) : null}
                  </div>
                )}
            </div>
          </div>
        }
        <span ref={queryStickyBarRef}></span>

        {hasMetrics(state) ? (
          <div className={queryTextBarStyle}>
            <div className="container-wrapper">
              <div className="row">
                <div className="col-12 ml-4">
                  <button
                    className="query-builder-scrolltop-button"
                    onClick={() => window.scrollTo(0, 0)}
                  >
                    <div className="query-insights-bar-text">
                      <FaPencilAlt className="mr-2" />{" "}
                      {state && state.queryTextVersion}
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>

      <div className="col-12 mt-3 pl-4">
        {!hasMetrics(state) ? (
          <QueryBuilderSegmentsLoader />
        ) : (
          renderVisualization()
        )}
      </div>
    </div>
  );
};

export default QueryResults;
